<template>
  <div class="centered-container">
    <ShareLink v-if="instanceId" :instanceId="instanceId" />
    <h2 class="promo-heading">
      Add things to pick from.
    </h2>

    <h2 class="sub-heading">{{instanceData.description}}</h2>

    <!-- List of Things Box -->
    <div class="content-box">
      <div class="box-heading">
        <h3>Things</h3>
      </div>
      <div class="box-content">
        <ul>
          <li v-for="thing in instanceData.things" :key="thing.id">
            <div class="list-item">
              <span>{{ thing.thing }}</span>
              <span class="actions">
                <button @click="editHandler(thing)">Edit</button>
                <button @click="deleteHandler(thing)">Delete</button>
              </span>
            </div>
          </li>
        </ul>
        <div class="input-container">
          <input
            v-model="newThing"
            type="text"
            placeholder="Enter a thing"
            @keyup.enter="addHandler"
          />
          <button @click="addHandler">Add</button>
        </div>
      </div>
    </div>

    <button @click="finishList" class="finish-button" :disabled="!canFinish || isSaving">
      {{ isSaving ? 'Saving...' : 'Done adding things' }}
    </button>
    <p v-if="!canFinish && finishDisabledReason" class="disabled-reason">{{ finishDisabledReason }}</p>

    <ParticipantList :instanceData="instanceData" />

    <!-- Edit Modal -->
    <ModalDialog
      :show="showEditModal"
      type="edit"
      title="Edit Thing"
      :initialValue="editingThing?.thing || ''"
      confirmText="Save"
      @confirm="handleEditConfirm"
      @cancel="handleEditCancel"
    />

    <!-- Delete Confirmation Modal -->
    <ModalDialog
      :show="showDeleteModal"
      type="confirm"
      title="Delete Thing"
      :message="deleteMessage"
      confirmText="Delete"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script>
import ParticipantList from "@/components/ParticipantList.vue";
import ShareLink from "@/components/ShareLink.vue";
import ModalDialog from "@/components/Modal.vue";

export default {
  components: {
    ParticipantList,
    ShareLink,
    ModalDialog
  },
  props: {
    instanceData: {
      type: Object,
      default: null
    },
    name: {
      type: Object,
      default: null
    },
    instanceId: {
      type: String,
      default: null
    },
    addThing: {
      type: Function,
      default: null
    },
    editThing: {
      type: Function,
      default: null
    },
    removeThing: {
      type: Function,
      default: null
    },
    updateState: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      newThing: "",
      movedOn: false,
      showEditModal: false,
      showDeleteModal: false,
      editingThing: null,
      deletingThing: null,
      deleteMessage: "",
      isSaving: false
    };
  },
  mounted() {
    this.updateNameArrays(this.instanceData);
  },
  watch: {
    'instanceData': {
      handler(newInstanceData) {
        if (newInstanceData) {
          this.updateNameArrays(newInstanceData);
        }
      },
      deep: true, // This is required to watch for changes inside the array
      immediate: true, // Run immediately when component is created
    },
    'instanceData.state.moveOn': {
      handler() {
        // Explicitly update movedOn when moveOn array changes
        if (this.instanceData) {
          const wasMovedOn = this.movedOn;
          this.updateNameArrays(this.instanceData);
          // If movedOn state changed, reset isSaving (server response received)
          if (wasMovedOn !== this.movedOn && this.isSaving) {
            this.isSaving = false;
          }
        }
      },
      deep: true,
    },
    'name': {
      handler() {
        // Update movedOn when name changes (e.g., when user first joins)
        if (this.instanceData) {
          this.updateNameArrays(this.instanceData);
        }
      },
      immediate: true,
    },
  },
  computed: {
    canFinish() {
      // Button should be enabled if there are at least 2 things and user hasn't moved on yet
      if (!this.instanceData || !this.instanceData.things) {
        return false;
      }
      const thingsCount = Array.isArray(this.instanceData.things) ? this.instanceData.things.length : 0;
      // movedOn is already calculated based on whether this user's ID is in moveOn array
      return thingsCount >= 2 && !this.movedOn;
    },
    finishDisabledReason() {
      if (!this.instanceData || !this.instanceData.things) {
        return null;
      }
      const thingsCount = Array.isArray(this.instanceData.things) ? this.instanceData.things.length : 0;
      if (thingsCount < 2) {
        return `Add at least 2 things before finishing (currently ${thingsCount})`;
      }
      if (this.movedOn) {
        return 'You have already finished adding things';
      }
      return null;
    },
  },
  methods: {
    updateNameArrays(instanceData) {
      if (!instanceData || !instanceData.state) {
        this.movedOn = false;
        return;
      }
      // Check if this user has already moved on
      // Only check if we have a name, otherwise default to false
      if (this.name && this.name.id) {
        this.movedOn = Array.isArray(instanceData.state.moveOn) && instanceData.state.moveOn.includes(this.name.id);
      } else {
        this.movedOn = false;
      }
    },
    async addHandler() {
      if (this.newThing.trim() !== "") {
        const thingText = this.newThing.trim();
        // Optimistic update: add to local state immediately
        const newThing = {
          id: 'temp-' + Date.now(), // Temporary ID
          thing: thingText,
          addedBy: this.name.id
        };
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.things.push(newThing);
        this.newThing = ""; // Clear the input field
        
        // Send to server (will update with real ID when response comes back)
        await this.addThing(thingText);
      }
    },
    editHandler(thing) {
      this.editingThing = thing;
      this.showEditModal = true;
    },
    async handleEditConfirm(editedValue) {
      if (this.editingThing) {
        // Optimistic update: update local state immediately
        const thing = this.instanceData.things.find(t => t.id === this.editingThing.id);
        if (thing) {
          // eslint-disable-next-line vue/no-mutating-props
          thing.thing = editedValue;
        }
        // Send to server
        await this.editThing(this.editingThing.id, editedValue);
      }
      this.handleEditCancel();
    },
    handleEditCancel() {
      this.showEditModal = false;
      this.editingThing = null;
    },
    deleteHandler(thing) {
      let addedBy;
      for(const forName of this.instanceData.names)
      {
        if (forName.id === thing.addedBy)
        {
          addedBy = forName;
          break;
        }
      }
      this.deletingThing = thing;
      this.deleteMessage = `Are you sure you want to remove "${thing.thing}" added by ${addedBy.name}?`;
      this.showDeleteModal = true;
    },
    async handleDeleteConfirm() {
      if (this.deletingThing) {
        // Optimistic update: remove from local state immediately
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.things = this.instanceData.things.filter(t => t.id !== this.deletingThing.id);
        // Send to server
        await this.removeThing(this.deletingThing.id);
      }
      this.handleDeleteCancel();
    },
    handleDeleteCancel() {
      this.showDeleteModal = false;
      this.deletingThing = null;
      this.deleteMessage = "";
    },
    async finishList() {
      if (this.canFinish && !this.isSaving) {
        this.isSaving = true;
        await this.updateState();
        // isSaving will be reset when moveOn state changes (server response)
      }
    }
  }
};
</script>

<style scoped>

ul {
  list-style-type: none;
  padding: 0;
  margin-top: 10px;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
}

li:last-child .list-item {
  border-bottom: none;
}

.disabled-reason {
  margin-top: 10px;
  color: #666;
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

</style>
